import { useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getService } from "../data/services";
import { MaskLines, Overline, Reveal } from "../components/site/Motion";
import { EnquiryForm } from "../components/forms/EnquiryForm";
import { ServiceFaq } from "../components/service/ServiceFaq";
import { NextServices } from "../components/service/NextServices";
import { EASE } from "../lib/api";

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  if (!s) return <Navigate to="/" replace />;

  return (
    <main key={s.slug} data-testid={`service-page-${s.slug}`}>
      <section ref={ref} className="grain relative overflow-hidden bg-[var(--ink)] pb-20 pt-36 md:pt-44">
        <div className="relative mx-auto max-w-[1440px] px-5 md:px-10">
          <Overline>Service {s.code} / 08</Overline>
          <MaskLines as="h1" onLoad delay={0.15} lines={s.title.split(" ")} testId="service-title"
            lineClass={(i) => (i % 2 === 1 ? "text-outline" : "")}
            className="mt-6 font-display text-[16vw] font-black uppercase leading-[0.84] md:text-8xl lg:text-[8.5rem]" />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.6 }}
            className="mt-8 max-w-xl text-lg text-[var(--bone-2)] md:text-xl" data-testid="service-tagline">{s.tagline}</motion.p>
          <motion.div initial={{ clipPath: "inset(0 100% 0 0)" }} animate={{ clipPath: "inset(0 0% 0 0)" }} transition={{ duration: 1.4, ease: EASE, delay: 0.4 }}
            className="clip-notch mt-14 aspect-[4/3] overflow-hidden md:aspect-[21/9]">
            <motion.img src={s.img} alt={s.title} style={{ y, scale: 1.2 }} className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>
      <section className="bg-[var(--bone)] py-24 text-[var(--ink)] md:py-32">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[#8a5a00]">Overview</p>
            <p className="mt-6 text-2xl leading-snug md:text-3xl" data-testid="service-intro">{s.intro}</p>
          </Reveal>
          <ul className="lg:col-span-6 lg:col-start-7" data-testid="service-includes">
            <li className="overline pb-4 text-[#8a5a00]">What's included</li>
            {s.includes.map((item, i) => (
              <Reveal key={item} delay={i * 0.05}>
                <div className="flex items-baseline gap-6 border-t border-[rgba(14,15,12,0.18)] py-5">
                  <span className="font-mono text-sm text-[#8a5a00]">0{i + 1}</span>
                  <span className="font-display text-2xl font-extrabold uppercase md:text-3xl">{item}</span>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>
      <ServiceFaq faqs={s.faqs} />
      <section className="bg-[var(--ink)] py-24 md:py-32" id="enquire">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Overline>Enquire</Overline>
            <MaskLines lines={["Get a quote", `for ${s.short}.`]} className="mt-6 font-display text-5xl font-black uppercase leading-[0.9] md:text-6xl" />
          </div>
          <Reveal className="lg:col-span-7 lg:col-start-6"><EnquiryForm defaultService={s.title} /></Reveal>
        </div>
      </section>
      <NextServices current={s.slug} />
    </main>
  );
}
